import { FormikActions } from 'formik';
import React, { ReactNode } from 'react';
import Form from '../../../app/common/components/Form';
import { IAddress, IAddressRequest, IAddressUpdateRequest } from '../address/address.interface';
import { AddressFormFieldsJP } from './AddressFormFieldsJP';
import { SupportedClientCountryCode } from '../../../config/constants/supportedClientCountryCode.enum';
import { FormManditoryAstrixMessage } from '../../../app/common/components/FormManitoryMessage';
import { getChallengeData } from '../../../utils/challengerMain';
import { TextHelpers } from '../../../utils/textHelpers';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';
import { ICountryMap } from '../../../app/common/fields/country/country';

interface IAddressFormProps {
    address?: IAddress;
    addAddress: (address: IAddressRequest) => any;
    isOnlyAddress: boolean;
    loading: boolean;
    updateAddress: (addressUuid: string, updatedAddress: IAddressUpdateRequest) => any;
    onSubmit: () => any;
    showWarning: (message: ReactNode) => any;
    defaultCountry: SupportedClientCountryCode;
    challengerPid: string;
    locale: SupportedLanguage;
    countriesMap: ICountryMap;
    stateInputDisabled: boolean;
    resetStateInputDisabledStatus: () => void;
    challengerDegenerate?: boolean;
}

interface IAddressFormState {
    initialValues: IAddressRequest;
    isEditing: boolean;
    isShippingAddress: boolean;
    isSubmitting: boolean;
}

class AddressFormJP extends React.Component<IAddressFormProps, IAddressFormState> {
    constructor(props: IAddressFormProps) {
        super(props);

        const { address, loading, isOnlyAddress, defaultCountry, challengerPid } = this.props;

        const initialValues: IAddressRequest = address
            ? {
                  firstName: address.firstName,
                  lastName: address.lastName,
                  companyName: address.companyName || '',
                  countryCode: address.countryCode || defaultCountry,
                  addressLine1: address.addressLine1,
                  addressLine2: address.addressLine2 || '',
                  city: address.city,
                  cityCode: address.cityCode || '',
                  postalCode: address.postalCode,
                  phoneNumber: address.phoneNumber || '',
                  state: address.state,
                  stateCode: address.stateCode,
                  isShipping: address.isShipping,
                  mobileEmail: address.mobileEmail || '',
                  positionName: address.positionName || '',
                  mobilePhoneNumber: address.mobilePhoneNumber || '',
                  faxNumber: address.faxNumber || '',
              }
            : {
                  firstName: '',
                  lastName: '',
                  companyName: '',
                  countryCode: defaultCountry,
                  addressLine1: '',
                  addressLine2: '',
                  city: '',
                  cityCode: '',
                  postalCode: '',
                  phoneNumber: '',
                  state: '',
                  stateCode: '',
                  isShipping: isOnlyAddress,
                  mobileEmail: '',
                  positionName: '',
                  mobilePhoneNumber: '',
                  faxNumber: '',
              };

        this.state = {
            initialValues,
            isEditing: Boolean(address),
            isShippingAddress: Boolean(address && address.isShipping),
            isSubmitting: loading,
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderFormFields = this.renderFormFields.bind(this);
        this.props.resetStateInputDisabledStatus();
    }

    public componentDidUpdate(prevProps: Readonly<IAddressFormProps>): void {
        const { loading } = this.props;
        if (loading !== prevProps.loading) {
            this.setState({ isSubmitting: loading });
        }
    }

    public handleSubmit(values: IAddressRequest, actions: FormikActions<IAddressRequest>) {
        const { onSubmit, address, addAddress, updateAddress, challengerPid } = this.props;
        const { isEditing } = this.state;
        const challengeData = getChallengeData(challengerPid);

        if (isEditing && address) {
            updateAddress(address.addressUuid, {
                ...values,
                mobileEmail: values.mobileEmail?.toLowerCase(),
                state: TextHelpers.getForeignPostValue(values.state),
                challengeData: challengeData,
                isShipping:
                    values.isShipping === address.isShipping ? undefined : values.isShipping,
            });
        } else {
            addAddress({
                ...values,
                mobileEmail: values.mobileEmail?.toLowerCase(),
                state: TextHelpers.getForeignPostValue(values.state),
                challengeData: challengeData,
            });
        }

        onSubmit();
    }

    public render() {
        const { showWarning, challengerPid, challengerDegenerate } = this.props;
        const { initialValues, isEditing, isSubmitting } = this.state;

        return (
            <Form
                buttonTextLabel={isEditing ? 'button.save' : 'button.add'}
                value={initialValues}
                handleSubmit={this.handleSubmit}
                loading={isSubmitting}
                renderFormFields={this.renderFormFields}
                showWarning={showWarning}
                saveButtonProps={{
                    qaId: 'submit-address',
                    width: '150px',
                }}
                challengerPid={challengerPid}
                qaId={`${isEditing ? 'edit' : 'create'}-address`}
            >
                <>
                    <FormManditoryAstrixMessage className={'address-jp-manditory-message'} />
                </>
            </Form>
        );
    }

    private renderFormFields(values: IAddressRequest) {
        return (
            <>
                <AddressFormFieldsJP
                    values={values}
                    stateInputDisabled={this.props.stateInputDisabled}
                />
            </>
        );
    }
}

export default AddressFormJP;
